import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import type { Challenge } from '../data/types';
import { kindOf } from '../data/road';
import { GOAL_ART, WINDOW, clamp, pieces, place, printOf, project, r2, spriteFor, spriteSrc, type Pt } from '../lib/depth-road';
import { ExerciseArt } from './ExerciseArt';
import { Signpost, type RoadStop } from './RoadMap';

/**
 * The road in depth (owner 2026-10-20, K49 on the AUX board): one picture, the
 * road climbing the painted hills into the distance, the stops shrinking as
 * they go, and your creative life at the end of it — a tree on the far hill
 * where Mrs. Jennys reads what she made to her friends.
 *
 * Mrs. Jennys stands for you. Beside each stop ahead she is doing that stop's
 * kind of making (writing, drawing, telling a story…), and at the end of each
 * chapter she walks on with friends. Today's stop is lit with Kraben on it;
 * the stops ahead are sealed, a padlock in their kind's colour and no words.
 *
 * `camera` is how far along the road you are looking, in stops (0 = the first
 * stop nearest). The landing draws it still at 0; the Road tab moves it with
 * the page's own scroll (ScrollingRoad), so scrolling down walks you forward.
 *
 * Everything is placed in percent of the box and sized in container units, so
 * it fills whatever box it is given: the gap between the landing's line and
 * its button, or the Road tab's screen.
 */

/** the road's width (px) at the nearest */
const NEAR_WIDTH = 34;
/** Mrs. Jennys is drawn beside the stops this far ahead (nearer, the stop and its label have the room; further, she is a speck) */
const SPRITE_FROM = 0.4;
const SPRITE_TO = 4.2;
/** the goal is this many stops past the last one */
const GOAL_GAP = 1.6;

const at = (x: number, y: number, extra?: Record<string, number>) => ({ left: `${r2(x)}%`, top: `${r2(y)}%`, ...extra }) as CSSProperties;

function Lock() {
  return (
    <svg className="dr-lock" viewBox="0 0 24 24" aria-hidden>
      <path d="M7.5 10.5V8a4.5 4.5 0 0 1 9 0v2.5" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
      <rect x="5" y="10" width="14" height="10.5" rx="2.6" fill="currentColor" />
      <circle cx="12" cy="15.2" r="1.7" fill="#F4EDE0" />
    </svg>
  );
}

export interface Gate { at: number; n: number; lesson: string }

export function DepthRoad({ stops, camera, goal, goalDay, nextLine, tomorrow, ahead = 3, gates = [], landed, firstN = 1, onStart, onTurn }: {
  stops: RoadStop[];
  camera: number;
  goal: { line: string };
  /** a pill under the tree, "Day 21" on the landing */
  goalDay?: string;
  /** under today's stop's title, before its kind and minutes: "Today" */
  nextLine: string;
  /** over the first sealed stop after today's */
  tomorrow?: string;
  /** how many stops past today's say their names (a short box has room for fewer) */
  ahead?: number;
  /** a chapter's gate stands before stop `at` */
  gates?: Gate[];
  landed?: string;
  /** the number on the first stop */
  firstN?: number;
  /** without it the road is shown, not used: nothing opens */
  onStart?: (c: Challenge, how: 'next' | 'again' | 'pick') => void;
  onTurn?: (s: RoadStop) => void;
}) {
  const [open, setOpen] = useState<RoadStop | null>(null);
  const last = stops.length - 1;
  const nextAt = stops.findIndex((s) => s.state === 'next');
  // only one thing moves at a time: a print waiting to be turned over, else today's stop (owner 2026-10-20)
  const dryAt = stops.findIndex((s) => printOf(s) === 'dry');
  const livelyAt = dryAt >= 0 ? dryAt : nextAt;
  // the one you did last, before today's
  let recentAt = -1;
  for (let i = (nextAt >= 0 ? nextAt : stops.length) - 1; i >= 0; i--) if (stops[i].state === 'done') { recentAt = i; break; }
  // a few words over the stops either side of today's: the next two or three by name, and the one you did last
  // (a print waiting on it says so, and a tap on it turns it over)
  // under each name, what kind of making it is and how long, so you know what to expect
  const what = (c: Challenge, when?: string) => [when, kindOf(c).label, `${c.minutes} min`].filter(Boolean).join(' · ');
  const bubbleFor = (s: RoadStop, i: number, print: ReturnType<typeof printOf>): { eyebrow?: string; text: string; meta?: string } | null => {
    if (print === 'dry') return { eyebrow: 'Your picture is ready', text: 'Tap to turn it over' };
    if (print === 'developing') return { eyebrow: s.c.title, text: 'Your crew are developing it…' };
    if (i === recentAt) return { text: s.c.title, meta: what(s.c, 'Done') };
    if (nextAt >= 0 && i > nextAt && i <= nextAt + ahead && s.state === 'later') return { text: s.c.title, meta: what(s.c, i === nextAt + 1 ? tomorrow : undefined) };
    return null;
  };
  const chapterEnd = (i: number) => i === last || gates.some((g) => g.at === i + 1);

  // the road: from below the screen through every stop in view, then dotted to the goal
  const seen = stops.map((_, i) => place(i, camera));
  const from = Math.max(0, seen.findIndex((q) => q.t >= -1.4));
  let to = seen.length - 1;
  while (to > from && seen[to].t > WINDOW) to--;
  const start = place(from - 1, camera);
  const pts: Pt[] = [[start.x, start.y], ...seen.slice(from, to + 1).map((q): Pt => [q.x, q.y])];
  const ws = [start.p, ...seen.slice(from, to + 1).map((q) => q.p)];
  // the goal keeps to the far hill until you are nearly there, then comes on fast
  const g = project((last + GOAL_GAP - camera) * 2.5);
  const goalFoot = Math.max(42, g.y);
  const goalW = Math.max(30, 140 * g.p);
  // the rest of the road: dotted to the tree while stops are still out of sight, the road itself once the last one is in view
  const home = to === last;
  if (home) { pts.push([50, goalFoot - 2]); ws.push(Math.max(0.15, g.p * 0.5)); }
  const road = pieces(pts);
  const width = (j: number) => Math.max(3, Math.round(NEAR_WIDTH * (ws[j] + ws[j + 1]) / 2));
  const end = pts[pts.length - 1];
  const beyond = home ? [] : pieces([pts[pts.length - 2] ?? end, end, [50, goalFoot - 1.5], [50, goalFoot - 4]]).slice(1, 2);

  return (
    <div className="dr">
      <img className="dr-bg" src="/art/road/road-bg.jpg" alt="" draggable={false} />
      <svg className="dr-path" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
        {beyond.map((d) => <path key="far" d={d} className="dr-far" />)}
        {road.map((d, j) => <path key={`e${j}`} d={d} className="dr-edge" style={{ strokeWidth: width(j) + 4 }} />)}
        {road.map((d, j) => <path key={`w${j}`} d={d} className="dr-way" style={{ strokeWidth: width(j) }} />)}
      </svg>

      <div className="dr-goal" style={at(50, goalFoot, { '--w': r2(goalW) })}>
        <span className="dr-glow" />
        <img src={GOAL_ART} alt={goal.line} draggable={false} />
        {goalDay && <span className="dr-goal-day">{goalDay}</span>}
      </div>

      {gates.map((gate) => {
        const a = place(gate.at - 1, camera);
        const b = place(gate.at, camera);
        const t = gate.at - 0.5 - camera;
        // only once it is near: far off, it would stand in front of the tree
        if (t < -0.4 || t > 2.4) return null;
        const { p } = project(t);
        return (
          <div key={gate.n} className="dr-gate" style={at((a.x + b.x) / 2, (a.y + b.y) / 2, { '--s': r2(58 * p), zIndex: Math.round(100 - t * 10), opacity: r2(clamp((2.4 - t) / 0.6, 0, 1)) })}>
            <img src="/art/road/gate.webp" alt="" draggable={false} />
            <span className="road-gate-banner"><small>Chapter {gate.n}</small>{gate.lesson}</span>
          </div>
        );
      })}

      {stops.map((s, i) => {
        const q = seen[i];
        if (q.t < -1.4 || q.t > WINDOW || q.y > 112) return null;
        const fade = clamp(WINDOW - q.t, 0, 1);
        const z = Math.round(100 - q.t * 10);
        const print = printOf(s);
        const kind = kindOf(s.c);
        const left = q.x < 50;
        const sprite = s.state !== 'next' && q.t >= SPRITE_FROM && q.t <= SPRITE_TO ? spriteFor(s.c, i, chapterEnd(i)) : null;
        const sx = clamp(q.x + (left ? -1 : 1) * q.s * 0.95, 10, 90);
        const n = firstN + i;
        const words = bubbleFor(s, i, print);
        const label = `${s.c.title}${print === 'dry' ? ', a print is dry: turn it over' : print === 'developing' ? ', your crew are developing it' : s.state === 'done' ? ', done' : s.state === 'next' ? ', next' : ', not yet'}`;
        return (
          <div key={s.c.id} className={`dr-at ${i === livelyAt ? 'lively' : 'still'}`} style={{ zIndex: z, opacity: fade < 1 ? r2(fade) : undefined }}>
            {sprite && <img className="dr-jennys" src={spriteSrc(sprite)} alt="" draggable={false} style={at(sx, q.y + q.s * 0.3, { '--s': r2(q.s) })} />}
            <button
              type="button"
              className={`dr-stop ${s.state} ${s.state === 'later' ? kind.tone : ''} ${print ?? ''} ${landed === s.c.id ? 'just-landed' : ''}`}
              style={at(q.x, q.y, { '--s': r2(q.s) })}
              tabIndex={onStart ? 0 : -1}
              aria-label={label}
              onClick={onStart ? () => (print === 'dry' ? onTurn?.(s) : setOpen(s)) : undefined}
            >
              {print === 'dry' ? <span className="road-print-back" />
                : print === 'developing' ? <><ExerciseArt id={s.c.id} className="road-stop-art" /><span className="road-safelight"><span className="dot pulse" /><span className="dot pulse d2" /><span className="dot pulse d3" /></span></>
                : s.state === 'done' ? (s.picture ? <img src={s.picture} alt="" /> : <ExerciseArt id={s.c.id} className="road-stop-art" />)
                : s.state === 'next' ? <span className="dr-n">{n}</span>
                : <Lock />}
              {s.state === 'done' && !print && <span className="road-tick">✓</span>}
              {(s.state === 'next' || print === 'dry') && <span className="tap-ring" />}
              {s.state === 'next' && <img className="dr-you" src={spriteSrc('ready')} alt="" draggable={false} />}
              {s.state === 'next' && <img className="dr-kraben" src="/art/road/kraben-wave.webp" alt="" draggable={false} />}
              {words && q.t > -0.6 && q.t < 3.6 && (
                <span className={`dr-bubble ${left ? 'to-r' : 'to-l'} ${print === 'dry' ? 'ready' : ''}`}>
                  {words.eyebrow && <small>{words.eyebrow}</small>}
                  {words.text}
                  {words.meta && <small className="dr-meta">{words.meta}</small>}
                </span>
              )}
            </button>
            {s.state === 'next' && q.t < 3 && (
              <div className={`dr-label ${left ? 'right-of' : 'left-of'}`} style={at(q.x, q.y, { '--s': r2(q.s) })}>
                <b>{s.c.title}</b>
                <span>{what(s.c, nextLine)}</span>
              </div>
            )}
          </div>
        );
      })}

      {/* over everything: the road is a stacking context of its own, and the tabs sit above it */}
      {open && onStart && createPortal(<Signpost stop={open} n={firstN + stops.indexOf(open)} onClose={() => setOpen(null)} onStart={onStart} />, document.body)}
    </div>
  );
}

/** Scroll this far (px) to walk one stop. */
const STEP = 140;

/**
 * The Road tab's road: the screen's own scroll area scrolls (the page itself
 * never does: the header and the tabs stay put), the road stays (a sticky
 * stage inside a tall box) and the scroll walks the camera forward, stop by
 * stop, until you are at the tree. "Do one again" and the deck follow it.
 */
export function ScrollingRoad({ stops, start, onCamera, jump, caption, ...rest }: Omit<Parameters<typeof DepthRoad>[0], 'camera'> & {
  /** over the sky: which chapter you are walking, and its lesson */
  caption?: string;
  /** where to stand when the page opens, in stops */
  start: number;
  /** told where the camera is, for the header */
  onCamera?: (camera: number) => void;
  /** set to a stop index to walk there */
  jump?: { to: number } | null;
}) {
  const box = useRef<HTMLDivElement>(null);
  const max = stops.length - 1 + 0.6;
  const [camera, setCamera] = useState(() => clamp(start, 0, max));
  // the screen's own scroll area (Frame theme "fit": the header and the tabs stay put, only this moves)
  const [stageH, setStageH] = useState<number | null>(null);
  const touched = useRef(false);
  // the road scrolls: say so until they have walked a little of it themselves (where it opens, and any jump the
  // app makes while it settles, do not count)
  const [walked, setWalked] = useState(false);
  const from = useRef<number | null>(null);
  const rootOf = () => (box.current?.closest('.main') as HTMLElement | null) ?? null;
  const read = useCallback(() => {
    const el = box.current;
    const root = el?.closest('.main');
    if (!el || !root) return;
    const c = clamp((root.getBoundingClientRect().top - el.getBoundingClientRect().top) / STEP, 0, max);
    setCamera((was) => (Math.abs(was - c) < 0.002 ? was : c));
    if (!touched.current) from.current = c;
    else if (from.current !== null && Math.abs(c - from.current) > 0.4) setWalked(true);
    onCamera?.(c);
  }, [max, onCamera]);
  const topOf = (c: number) => {
    const root = rootOf();
    if (!root || !box.current) return 0;
    return root.scrollTop + box.current.getBoundingClientRect().top - root.getBoundingClientRect().top + c * STEP;
  };


  // open where the person is on the road, and follow it while it settles (the profile can arrive a moment after the
  // first render) — until they touch it
  useEffect(() => {
    const mine = () => { touched.current = true; };
    for (const e of ['touchstart', 'wheel', 'keydown', 'pointerdown'] as const) window.addEventListener(e, mine, { passive: true });
    return () => { for (const e of ['touchstart', 'wheel', 'keydown', 'pointerdown'] as const) window.removeEventListener(e, mine); };
  }, []);
  useLayoutEffect(() => {
    const go = () => { const root = rootOf(); if (root && !touched.current && start > 0) root.scrollTop = topOf(clamp(start, 0, max)); read(); };
    go();
    const t = setTimeout(go, 80);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, stageH]);
  useEffect(() => {
    const root = rootOf();
    if (!root) return;
    let raf = 0;
    const on = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(read); };
    const size = () => { setStageH(root.clientHeight); on(); };
    size();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(size) : null;
    ro?.observe(root);
    root.addEventListener('scroll', on, { passive: true });
    window.addEventListener('resize', size);
    return () => { cancelAnimationFrame(raf); ro?.disconnect(); root.removeEventListener('scroll', on); window.removeEventListener('resize', size); };
  }, [read]);
  useEffect(() => {
    const root = rootOf();
    if (jump && root) root.scrollTo({ top: topOf(clamp(jump.to, 0, max)), behavior: 'smooth' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jump]);

  return (
    <div className="dr-scroller" ref={box} style={{ '--walk': `${Math.round(max * STEP)}px`, ...(stageH ? { '--stage': `${stageH}px` } : {}) } as CSSProperties}>
      <div className="dr-stage">
        <DepthRoad stops={stops} camera={camera} {...rest} />
        {caption && <span className="dr-chapter">{caption}</span>}
        {!walked && camera < max - 0.5 && <span className="dr-hint" aria-hidden="true"><span className="dr-hint-arrow">↓</span>Scroll down to walk the road</span>}
        {walked && camera < max - 0.5 && <span className="dr-more" aria-hidden="true">⌄</span>}
        <span className="dr-rail" aria-hidden="true"><span style={{ top: `${r2((camera / max) * 84)}%` }} /></span>
      </div>
    </div>
  );
}
